import { AuthStorage } from "utils/auth-storage";
import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useParams } from "react-router-dom";
import { WarehouseServices } from "apis/Services/WarehouseService";
import * as XLSX from "xlsx";
import {
  Button,
  Dropdown,
  Modal,
  Table,
  Tag,
  Form,
  Select,
  InputNumber,
  Progress,
  Input,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  MoreOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import toast from "react-hot-toast";
import LoadingComponent from "components/loading-component";
import ProcoreLogo from "assets/images/Procore_Logo.svg";
import MaterialFormModal from "./materialFormModal";

const EXPORTABLE_FILTER_KEYS = [
  "sku",
  "itemVendor",
  "category",
  "subcategory",
  "itemBay",
  "itemRow",
  "itemShelf",
  "storageLocation",
];

const getComparableValue = (record, field) => {
  switch (field) {
    case "currentStock":
      return Number(record?.currentStock || 0);
    case "allocatedStock":
      return Number(record?.allocatedStock ?? -Infinity);
    case "unallocatedStock":
      return Number(record?.unallocatedStock ?? -Infinity);
    case "unitPrice":
      return Number(record?.unitPrice || 0);
    case "totalValue":
      return Number(record?.currentStock || 0) * Number(record?.unitPrice || 0);
    default:
      return String(record?.[field] || "").toLowerCase();
  }
};

const formatStockAmount = (value, unit) => {
  if (value === null || value === undefined) {
    return "-";
  }

  return `${value}${unit ? ` ${unit}` : ""}`;
};

const normalizeActivityHistory = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  return Array.isArray(response?.content) ? response.content : [];
};

const applyTableState = (rows, filters = {}, sorter = {}) => {
  let nextRows = Array.isArray(rows) ? [...rows] : [];

  EXPORTABLE_FILTER_KEYS.forEach((filterKey) => {
    const selectedValues = filters?.[filterKey];

    if (Array.isArray(selectedValues) && selectedValues.length > 0) {
      nextRows = nextRows.filter((row) =>
        selectedValues.includes(row?.[filterKey]),
      );
    }
  });

  if (sorter?.field && sorter?.order) {
    nextRows.sort((left, right) => {
      const leftValue = getComparableValue(left, sorter.field);
      const rightValue = getComparableValue(right, sorter.field);

      if (leftValue < rightValue) {
        return sorter.order === "ascend" ? -1 : 1;
      }

      if (leftValue > rightValue) {
        return sorter.order === "ascend" ? 1 : -1;
      }

      return 0;
    });
  }

  return nextRows;
};

const MaterialsTable = forwardRef(
  (
    {
      data = [],
      categoryOptions = [],
      subcategoryOptions = [],
      locationsOptions = [],
      unitsOptions = [],
      onDataUpdate,
      showTransferModal,
      showPostToProcoreModal,
      searchText = "",
      loading = false,
      showActions = true,
      customColumns = [],
      rowSelection = null,
      onAddNew,
      showPagination = true,
      showAllocationColumns = true,
      accessLevel,
    },
    ref,
  ) => {
    const { company_id, id } = useParams();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [tableFilters, setTableFilters] = useState({});
    const [tableSorter, setTableSorter] = useState({});
    const [form] = Form.useForm();

    const handleDeleteItem = (record) => {
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");
      let ReqObj = {
        company: company_id,
        project: id,
        code,
        loginEmail: email,
        itemID: record.itemID,
      };
      WarehouseServices.deleteWarehouseItem(ReqObj)
        .then((res) => {
          toast.success("Material deleted successfully");
          if (onDataUpdate) onDataUpdate();
        })
        .catch((err) => {
          console.log(err);
          toast.error("Something went wrong, please try again.");
          if (onDataUpdate) onDataUpdate();
        });
    };

    const getInventoryPurchaseHistory = (record) => {
      setIsLoadingHistory(true);
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");
      let ReqObj = {
        company: company_id,
        code,
        loginEmail: email,
        itemID: record.itemID,
      };
      WarehouseServices.getInventoryPurchaseHistory(ReqObj)
        .then((res) => {
          setHistoryData(normalizeActivityHistory(res));
          setShowHistoryModal(true);
        })
        .catch((err) => {
          console.log(err);
          toast.error("Failed to load change history");
        })
        .finally(() => setIsLoadingHistory(false));
    };

    const handleCancel = () => {
      form.resetFields();
      setIsModalVisible(false);
      setSelectedItem(null);
      setIsEditing(false);
    };

    const handleHistoryModalClose = () => {
      setShowHistoryModal(false);
      setHistoryData([]);
      // setSelectedItem(null);
    };

    const handleSubmit = (values) => {
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");

      const ReqObj = isEditing
        ? {
            company: company_id,
            itemID: selectedItem.itemID,
            code,
            loginEmail: email,
            values,
          }
        : {
            company: company_id,
            code,
            loginEmail: email,
            values,
          };

      const serviceCall = isEditing
        ? WarehouseServices.updateWarehouseItem
        : WarehouseServices.createWarehouseItem;

      serviceCall(ReqObj)
        .then(() => {
          toast.success(isEditing ? "Material updated!" : "Material created!");
          onDataUpdate?.();
        })
        .catch(() => {
          toast.error("Something went wrong");
        });

      setIsModalVisible(false);
      setSelectedItem(null);
      setIsEditing(false);
    };

    const handleEdit = (record) => {
      setSelectedItem(record);
      setIsEditing(true);
      setIsModalVisible(true);

      form.setFieldsValue({
        materialName: record.materialName,
        sku: record.sku,
        itemVendor: record.itemVendor,
        category: record.category,
        subcategory: record.subcategory,
        description: record.description,
        unitPrice: record.unitPrice,
        unit: record.unit,
        currentStock: record.currentStock,
        minStock: record.minStock,
        maxStock: record.maxStock,
        itemBay: record.itemBay,
        itemShelf: record.itemShelf,
        itemRow: record.itemRow,
        storageLocation: record.storageLocation,
      });
    };

    const handleTransfer = (record) => {
      setSelectedItem(record);
      if (showTransferModal) {
        showTransferModal(record);
      }
    };

    const handlePostToProcore = (record) => {
      setSelectedItem(record);
      if (showPostToProcoreModal) {
        showPostToProcoreModal(record);
      }
    };

    const handleDelete = (record) => {
      setSelectedItem(record);

      Modal.confirm({
        title: "Are you sure you want to delete this material?",
        content: `${
          record.materialName || "This material"
        } will be permanently deleted.`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          handleDeleteItem(record);
        },
      });
    };

    const handleAddNew = () => {
      setSelectedItem(null);
      setIsEditing(false);
      setIsModalVisible(true);
      form.resetFields();
      if (onAddNew) onAddNew(); // Call parent callback if provided
    };

    const handlePriceHistory = () => {
      if (selectedItem) {
        getInventoryPurchaseHistory(selectedItem);
      }
    };

    // Expose handleAddNew function to parent component via ref
    // Filter data based on search text
    const filteredData = data.filter((item) => {
      const search = searchText.toLowerCase();
      return (
        item.materialName?.toLowerCase().includes(search) ||
        item.sku?.toLowerCase().includes(search) ||
        item.itemVendor?.toLowerCase().includes(search) ||
        item.category?.toLowerCase().includes(search) ||
        item.subcategory?.toLowerCase().includes(search) ||
        item.description?.toLowerCase().includes(search) ||
        item.itemBay?.toLowerCase().includes(search) ||
        item.itemRow?.toLowerCase().includes(search) ||
        item.itemShelf?.toLowerCase().includes(search) ||
        item.storageLocation?.toLowerCase().includes(search)
      );
    });

    const exportableData = useMemo(
      () => applyTableState(filteredData, tableFilters, tableSorter),
      [filteredData, tableFilters, tableSorter],
    );

    // Create filter options
    const skuFilters = [...new Set(data.map((item) => item.sku))].map(
      (sku) => ({
        text: sku,
        value: sku,
      }),
    );

    const categoryFilters = [...new Set(data.map((item) => item.category))].map(
      (cat) => ({
        text: cat,
        value: cat,
      }),
    );

    const vendorFilters = [...new Set(data.map((item) => item.itemVendor))]
      .filter(Boolean)
      .map((vendor) => ({
        text: vendor,
        value: vendor,
      }));

    const subcategoryFilters = [
      ...new Set(data.map((item) => item.subcategory)),
    ].map((cat) => ({
      text: cat,
      value: cat,
    }));

    const locationFilters = [
      ...new Set(data.map((item) => item.storageLocation)),
    ].map((loc) => ({
      text: loc,
      value: loc,
    }));

    const bayFilters = [...new Set(data.map((item) => item.itemBay))].map(
      (bay) => ({
        text: bay,
        value: bay,
      }),
    );

    const rowFilters = [...new Set(data.map((item) => item.itemRow))].map(
      (row) => ({
        text: row,
        value: row,
      }),
    );

    const shelfFilters = [...new Set(data.map((item) => item.itemShelf))].map(
      (shelf) => ({
        text: shelf,
        value: shelf,
      }),
    );

    const handleExportToCsv = () => {
      if (exportableData.length < 1) {
        toast.error("No materials available to export.");
        return;
      }

      try {
        const exportRows = exportableData.map((item) => ({
          "Material Name": item.materialName || "",
          Description: item.description || "",
          SKU: item.sku || "",
          Vendor: item.itemVendor || "",
          Category: item.category || "",
          Subcategory: item.subcategory || "",
          "Current Stock": item.currentStock ?? "",
          "Min Stock": item.minStock ?? "",
          "Max Stock": item.maxStock ?? "",
          Unit: item.unit || "",
          "Unit Price": item.unitPrice ?? "",
          "Total Value":
            Number(item.currentStock || 0) * Number(item.unitPrice || 0),
          Bay: item.itemBay || "",
          Row: item.itemRow || "",
          Shelf: item.itemShelf || "",
          Location: item.storageLocation || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const exportUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const dateStamp = new Date().toISOString().slice(0, 10);

        link.href = exportUrl;
        link.download = `warehouse-inventory-${dateStamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(exportUrl);

        toast.success("Inventory exported to CSV!");
      } catch (error) {
        console.error("Inventory export error:", error);
        toast.error("Failed to export inventory to CSV.");
      }
    };

    useImperativeHandle(ref, () => ({
      openAddModal: handleAddNew,
      exportToCsv: handleExportToCsv,
    }));

    // Columns for the activity history table
    const activityHistoryColumns = [
      {
        title: "Activity Type",
        dataIndex: "movementType",
        key: "movementType",
        render: (activityType) => activityType || "-",
      },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        align: "right",
        sorter: (a, b) => Number(a.quantity || 0) - Number(b.quantity || 0),
        render: (quantity) => quantity ?? "-",
      },
      {
        title: "Unit Price",
        dataIndex: "unitPrice",
        key: "unitPrice",
        align: "right",
        sorter: (a, b) =>
          Number(a.unitPrice || 0) - Number(b.unitPrice || 0),
        render: (price) =>
          price === null || price === undefined
            ? "-"
            : new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(price),
      },
      {
        title: "Activity Date",
        dataIndex: "movementDate",
        key: "movementDate",
        render: (date) => date || "-",
      },
      {
        title: "From Location",
        dataIndex: "fromLocation",
        key: "fromLocation",
        render: (location) => location || "-",
      },
      {
        title: "To Location",
        dataIndex: "toLocation",
        key: "toLocation",
        render: (location) => location || "-",
      },
      {
        title: "To Sub-Location",
        dataIndex: "toSubLocation",
        key: "toSubLocation",
        render: (location) => location || "-",
      },
      {
        title: "Note",
        dataIndex: "note",
        key: "note",
        render: (note) => note || "-",
      },
    ];

    // Base columns
    const baseColumns = [
      {
        title: "Material",
        dataIndex: "materialName",
        key: "materialName",
        width: 250,
        sorter: (a, b) => a.materialName.localeCompare(b.materialName),
        render: (text, record) => (
          <div
            className=" pointer"
            onClick={() => {
              if (accessLevel > 1) {
                handleEdit(record);
              }
            }}
          >
            <p className="m-0 p-0 lh-1 fw-bold">{text}</p>
            <small className="m-0 p-0 opacity-50">{record.description}</small>
          </div>
        ),
      },
      {
        title: "SKU",
        dataIndex: "sku",
        key: "sku",
        width: 150,
        sorter: (a, b) => a.sku.localeCompare(b.sku),
        filters: skuFilters,
        onFilter: (value, record) => record.sku === value,
      },
      {
        title: "Vendor",
        dataIndex: "itemVendor",
        key: "itemVendor",
        width: 180,
        sorter: (a, b) =>
          String(a.itemVendor || "").localeCompare(String(b.itemVendor || "")),
        filters: vendorFilters,
        onFilter: (value, record) => record.itemVendor === value,
        render: (itemVendor) => itemVendor || "-",
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 150,
        sorter: (a, b) =>
          String(a.category || "").localeCompare(String(b.category || "")),
        filters: categoryFilters,
        onFilter: (value, record) => record.category === value,
        render: (category) => (
          <Tag className="rounded-5 fw-bold" bordered={false} color="blue">
            {category}
          </Tag>
        ),
      },
      {
        title: "Subcategory",
        dataIndex: "subcategory",
        key: "subcategory",
        width: 150,
        sorter: (a, b) =>
          String(a.subcategory || "").localeCompare(
            String(b.subcategory || ""),
          ),
        filters: subcategoryFilters,
        onFilter: (value, record) => record.subcategory === value,
        render: (subcategory) => (
          <Tag className="rounded-5 fw-bold" bordered={false} color="blue">
            {subcategory}
          </Tag>
        ),
      },
      {
        title: "Current Stock",
        dataIndex: "currentStock",
        key: "currentStock",
        width: 120,
        sorter: (a, b) => a.currentStock - b.currentStock,
        render: (_, record) => {
          const { currentStock, minStock, maxStock, unit } = record;

          const percent = maxStock > 0 ? (currentStock / maxStock) * 100 : 0;
          const clampedPercent = Math.min(Math.max(percent, 0), 100);
          const isLow = currentStock < minStock;

          return (
            <div className="d-flex align-items-center  w-100 gap-2">
              <div
                className={` ${isLow ? "text-danger" : ""}`}
                style={{ width: "90px" }}
              >
                {currentStock} {unit}
              </div>

              <div style={{ width: "70px" }}>
                <Progress
                  percent={clampedPercent}
                  style={{ width: "70px" }}
                  size="small"
                  showInfo={false}
                  strokeColor={isLow ? "#FFA500" : "#1777FF"}
                />
              </div>

              <div
                style={{ width: "40px" }}
                className="d-flex align-items-center"
              >
                {isLow && (
                  <>
                    <ExclamationCircleOutlined
                      style={{ color: "orange", fontSize: 14 }}
                    />
                    <small className="fw-bold ms-1" style={{ color: "orange" }}>
                      LOW
                    </small>
                  </>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: "Allocated Stock",
        dataIndex: "allocatedStock",
        key: "allocatedStock",
        width: 150,
        sorter: (a, b) =>
          Number(a.allocatedStock ?? -Infinity) -
          Number(b.allocatedStock ?? -Infinity),
        render: (allocatedStock, record) =>
          formatStockAmount(allocatedStock, record.unit),
      },
      {
        title: "Unallocated Stock",
        dataIndex: "unallocatedStock",
        key: "unallocatedStock",
        width: 160,
        sorter: (a, b) =>
          Number(a.unallocatedStock ?? -Infinity) -
          Number(b.unallocatedStock ?? -Infinity),
        render: (unallocatedStock, record) =>
          formatStockAmount(unallocatedStock, record.unit),
      },
      {
        title: "Unit Price",
        dataIndex: "unitPrice",
        key: "unitPrice",
        width: 120,
        sorter: (a, b) => a.unitPrice - b.unitPrice,
        render: (unitPrice, record) => (
          <>
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(unitPrice)}
          </>
        ),
      },
      {
        title: "Total Value",
        key: "totalValue",
        width: 130,
        sorter: (a, b) =>
          a.currentStock * a.unitPrice - b.currentStock * b.unitPrice,
        render: (_, record) => {
          const totalValue = record.currentStock * record.unitPrice;
          return (
            <span>
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(totalValue)}
            </span>
          );
        },
      },
      {
        title: "Bay",
        dataIndex: "itemBay",
        key: "itemBay",
        sorter: (a, b) => a.itemBay.localeCompare(b.itemBay),
        filters: bayFilters,
        onFilter: (value, record) => record.itemBay === value,
      },
      {
        title: "Row",
        dataIndex: "itemRow",
        key: "itemRow",
        sorter: (a, b) => a.itemRow.localeCompare(b.itemRow),
        filters: rowFilters,
        onFilter: (value, record) => record.itemRow === value,
      },
      {
        title: "Shelf",
        dataIndex: "itemShelf",
        key: "itemShelf",
        sorter: (a, b) => a.itemShelf.localeCompare(b.itemShelf),
        filters: shelfFilters,
        onFilter: (value, record) => record.itemShelf === value,
      },
      {
        title: "Location",
        dataIndex: "storageLocation",
        key: "storageLocation",
        // width: 160,
        sorter: (a, b) => a.storageLocation.localeCompare(b.storageLocation),
        filters: locationFilters,
        onFilter: (value, record) => record.storageLocation === value,
      },
    ];

    // Actions column
    const actionsColumn = {
      title: "",
      key: "action",
      width: 60,
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            icon: <EditOutlined />,
            label: "Edit",
            onClick: () => handleEdit(record),
          },
          {
            key: "transfer",
            icon: <SwapOutlined />,
            label: "Transfer",
            onClick: () => handleTransfer(record),
          },
          {
            key: "postToProcore",
            icon: <img src={ProcoreLogo} alt="Procore" width={16} />,
            label: "Create Purchase Order",
            onClick: () => handlePostToProcore(record),
          },
          {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Delete",
            danger: true,
            disabled: accessLevel === 2,
            onClick: () => handleDelete(record),
          },
        ];
        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            disabled={accessLevel < 2}
          >
            <Button type="text" shape="circle" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    };

    // Combine columns
    let columns = showAllocationColumns
      ? [...baseColumns]
      : baseColumns.filter(
          (column) =>
            column.key !== "allocatedStock" && column.key !== "unallocatedStock",
        );
    if (customColumns.length > 0) {
      columns = [...columns, ...customColumns];
    }
    if (showActions) {
      columns.push(actionsColumn);
    }

    return (
      <>
        <Table
          dataSource={filteredData}
          scroll={{ x: "max-content" }}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="large"
          bordered
          rowSelection={rowSelection}
          pagination={showPagination}
          onChange={(_, filters, sorter) => {
            setTableFilters(filters || {});

            if (Array.isArray(sorter)) {
              const activeSorter =
                sorter.find((item) => item?.order) || sorter[0] || {};
              setTableSorter(activeSorter);
              return;
            }

            setTableSorter(sorter || {});
          }}
        />

        <MaterialFormModal
          open={isModalVisible}
          isEditing={isEditing}
          selectedItem={selectedItem}
          categoryOptions={categoryOptions}
          subcategoryOptions={subcategoryOptions}
          unitsOptions={unitsOptions}
          locationsOptions={locationsOptions}
          loadingPrices={isLoadingHistory}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          onHistoryClick={handlePriceHistory}
        />

        <Modal
          title={`Change History - ${selectedItem?.materialName || "Material"}`}
          open={showHistoryModal}
          centered
          width={1100}
          onCancel={handleHistoryModalClose}
          footer={false}
        >
          {isLoadingHistory ? (
            <div className="d-flex flex-column align-items-center py-4">
              <LoadingComponent />
              <p className="text-muted mt-2">Loading activity history...</p>
            </div>
          ) : (
            <div>
              {historyData.length > 0 ? (
                <Table
                  dataSource={historyData}
                  columns={activityHistoryColumns}
                  className=" mt-3"
                  bordered
                  scroll={{ x: "max-content" }}
                  rowKey={(record, index) =>
                    record.movementID ||
                    `${record.movementDate}-${record.movementType}-${index}`
                  }
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                  }}
                />
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">
                    No activity history available for this material.
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal>
      </>
    );
  },
);

export default MaterialsTable;
